import fetchCache from '../utils/fetchCache.js';
import { getNsfwEnabled } from '../utils/userPrefs.js';
import {
  getFilteredNsfwPromptMessage,
  getFilteredUnrecognizedPromptMessage,
  getNsfwExactTags,
  getNsfwKeywords,
  loadNsfwKeywords,
  promptContainsNsfw,
} from '../utils/nsfwTags.js';
import { isBodyPromptToken, isLocationDenylistTag } from './bodyOptionCatalog.js';
import { tokenizePromptForHighlight } from './promptSyntax.js';

// Danbooru tag search with alias resolution and category filtering
let tagsData = null;
let tagIndex = null; // Fast lookup by tag name and aliases

// Default forbidden categories for non-outfit prompt types
const DEFAULT_FORBIDDEN_CATEGORIES = [1, 5];
const CHARACTER_FORBIDDEN_CATEGORIES = [6];
const OUTFIT_FORBIDDEN_CATEGORIES = [3, 4];
const SPRITE_ACTION_FORBIDDEN_CATEGORIES = [3, 4, 6];
const SHARED_FORBIDDEN_CATEGORIES = [7];
const DYNAMIC_CATEGORY_6_KEYWORDS = ['hat', 'shoe', 'jacket', 'shirt', 'sock', 'dress', 'skirt', 'uniform', 'underwear', 'panties', 'jeans', 'thighhigh', 'kneehigh', 'gloves'];
// Same list as CDN data/nsfw_keywords.json (explicit tags plus the SFW clothing denylist).
export let DYNAMIC_CATEGORY_7_KEYWORDS = [];
// Clothing stems stay category 6 so the unrestricted site can still put them in outfit prompts.
// SFW mode blocks them through promptContainsNsfw, which reads this same file.
const OUTFIT_CLOTHING_KEYWORDS = new Set(['lingerie', 'panties', 'thong', 'pasties', 'underwear', 'playboy', 'micro_bikini']);
let CATEGORY_7_ACTION_KEYWORDS = [];
// Exact full-tag matches that override partial keyword categorization (e.g. "panties_aside" is an action, not an outfit)
let DYNAMIC_CATEGORY_7_EXACT_TAGS = [];

const syncDynamicNsfwCategoryLists = () => {
  DYNAMIC_CATEGORY_7_KEYWORDS = getNsfwKeywords();
  DYNAMIC_CATEGORY_7_EXACT_TAGS = getNsfwExactTags();
  CATEGORY_7_ACTION_KEYWORDS = DYNAMIC_CATEGORY_7_KEYWORDS.filter((keyword) => !OUTFIT_CLOTHING_KEYWORDS.has(keyword));
};
const FAST_PREVIEW_FORBIDDEN_CATEGORIES = [8];
const APPEARANCE_COUNT_TAGS = new Set([
  '1girl', '2girls', '3girls', '4girls', '5girls', '6+girls', 'multiple_girls',
  '1boy', '2boys', '3boys', '4boys', '5boys', '6+boys', 'multiple_boys',
  '1other', '2others', '3others', '4others', '5others', '6+others', 'multiple_others',
]);
export const DYNAMIC_CATEGORY_8_KEYWORDS = ['loli', 'shota', 'aged down', 'toddler', 'baby', 'child', 'rape'];
/** Appearance tags that mark a public character as premium-gated (is_premium / isP). */
export const PREMIUM_APPEARANCE_KEYWORDS = ['loli', 'shota', 'aged down', 'toddler', 'baby'];

export const findPremiumAppearanceTag = (promptText) => {
  const lower = String(promptText || '').toLowerCase();
  if (!lower) return null;
  return PREMIUM_APPEARANCE_KEYWORDS.find((tag) => lower.includes(tag)) || null;
};

function getForbiddenCategories(options = {}) {
  if (options.suggestionType === 'negativePrompt') {
    const categories = [];
    if (!getNsfwEnabled() && options.allowNsfw !== true) categories.push(...SHARED_FORBIDDEN_CATEGORIES);
    return Array.from(new Set(categories));
  }

  if (options.replaceForbiddenCategories === true && Array.isArray(options.forbiddenCategories)) {
    const categories = [...options.forbiddenCategories];
    if (!getNsfwEnabled() && options.allowNsfw !== true) categories.push(...SHARED_FORBIDDEN_CATEGORIES);
    return Array.from(new Set(categories));
  }

  const mergedCategories = [...DEFAULT_FORBIDDEN_CATEGORIES];

  if (options.suggestionType === 'outfitPrompt') {
    mergedCategories.push(...OUTFIT_FORBIDDEN_CATEGORIES, ...SHARED_FORBIDDEN_CATEGORIES);
  } else if (options.suggestionType === 'characterPrompt') {
    mergedCategories.push(...CHARACTER_FORBIDDEN_CATEGORIES, ...SHARED_FORBIDDEN_CATEGORIES);
  } else if (options.suggestionType === 'spriteActionPrompt') {
    mergedCategories.push(...SPRITE_ACTION_FORBIDDEN_CATEGORIES);
    if (!getNsfwEnabled()) mergedCategories.push(...SHARED_FORBIDDEN_CATEGORIES);
  }

  if (Array.isArray(options.forbiddenCategories)) {
    mergedCategories.push(...options.forbiddenCategories);
  }

  return Array.from(new Set(mergedCategories));
}

function normalizeTagText(value) {
  if (typeof value !== 'string') {
    return '';
  }

  return value.toLowerCase().trim();
}

function matchesDynamicCategory(tagObj, keywords, mode = 'partial') {
  if (!tagObj || tagObj.c !== 0) {
    return false;
  }

  const canonicalTag = normalizeTagText(tagObj.t);
  const aliases = Array.isArray(tagObj.a) ? tagObj.a : [];

  return keywords.some((keyword) => {
    const normalizedKeyword = normalizeTagText(keyword);
    if (!normalizedKeyword) {
      return false;
    }

    if (mode === 'exact') {
      return canonicalTag === normalizedKeyword ||
        aliases.some((alias) => normalizeTagText(alias) === normalizedKeyword);
    }

    if (canonicalTag.includes(normalizedKeyword)) {
      return true;
    }

    return aliases.some((alias) => normalizeTagText(alias).includes(normalizedKeyword));
  });
}

function applyDynamicCategoryRules() {
  if (!Array.isArray(tagsData)) {
    return;
  }

  tagsData.forEach((tagObj) => {
    if (matchesDynamicCategory(tagObj, DYNAMIC_CATEGORY_7_EXACT_TAGS, 'exact')) {
      tagObj.c = 7;
      return;
    }

    if (matchesDynamicCategory(tagObj, DYNAMIC_CATEGORY_8_KEYWORDS)) {
      tagObj.c = 8;
      return;
    }

    if (matchesDynamicCategory(tagObj, CATEGORY_7_ACTION_KEYWORDS)) {
      tagObj.c = 7;
      return;
    }

    if (matchesDynamicCategory(tagObj, DYNAMIC_CATEGORY_6_KEYWORDS)
      || matchesDynamicCategory(tagObj, [...OUTFIT_CLOTHING_KEYWORDS])) {
      tagObj.c = 6;
    }
  });
}

async function loadDanbooruTags() {
  if (tagsData) {
    return tagsData;
  }
  await loadNsfwKeywords();
  if (tagsData) {
    return tagsData;
  }

  try {
    syncDynamicNsfwCategoryLists();
    tagsData = await fetchCache.fetchJSON('/assets/tags/danbooru-tags.jsonb');
    applyDynamicCategoryRules();
    buildTagIndex();
    return tagsData;
  } catch (error) {
    console.error('Error loading Danbooru tags:', error);
    return [];
  }
}

// Build index for O(1) tag/alias lookups
function buildTagIndex() {
  tagIndex = new Map();

  tagsData.forEach(tagObj => {
    // Index main tag
    tagIndex.set(tagObj.t.toLowerCase(), tagObj);

    // Index aliases pointing to main tag
    tagObj.a.forEach(alias => {
      tagIndex.set(alias.toLowerCase(), tagObj);
    });
  });
}

// @param {string} searchTag - Tag name or alias
// @returns {Object|null} Tag object or null
function getTagByNameOrAlias(searchTag) {
  if (!tagIndex) {
    return null;
  }

  return tagIndex.get(searchTag.toLowerCase()) || null;
}

const RATING_PROMPT_TAGS = new Set(['general', 'sensitive', 'nsfw', 'explicit']);

function isRatingPromptTag(tagName) {
  const lookup = normalizeTagForLookup(tagName).toLowerCase();
  return Boolean(lookup) && RATING_PROMPT_TAGS.has(lookup);
}

// @param {string} tagName - Tag name or alias
// @returns {boolean} True if the tag exists in the Danbooru index
function isRecognizedTag(tagName) {
  return Boolean(getTagByNameOrAlias(tagName));
}

// @param {string} tagName - Tag name or alias
// @returns {boolean} True if forbidden for provided context
const promptTokenIsFilteredNsfw = (tagName) => {
  const lookup = normalizeTagForLookup(tagName);
  if (promptContainsNsfw(tagName) || (lookup && promptContainsNsfw(lookup))) return true;
  const tagObj = lookup ? getTagByNameOrAlias(lookup) : null;
  if (!tagObj) return false;
  if (SHARED_FORBIDDEN_CATEGORIES.includes(tagObj.c)) return true;
  if (promptContainsNsfw(tagObj.t)) return true;
  const aliases = Array.isArray(tagObj.a) ? tagObj.a : [];
  return aliases.some((alias) => promptContainsNsfw(alias));
};

function isAppearanceCountTag(tagName) {
  const lookup = normalizeTagForLookup(tagName).toLowerCase();
  if (!lookup) return false;
  if (APPEARANCE_COUNT_TAGS.has(lookup)) return true;
  const tagObj = getTagByNameOrAlias(lookup);
  return Boolean(tagObj && APPEARANCE_COUNT_TAGS.has(String(tagObj.t || '').toLowerCase()));
}

function isForbiddenTag(tagName, options = {}) {
  if (options.forbidAppearanceCountTags === true && isAppearanceCountTag(tagName)) return true;
  const lookup = normalizeTagForLookup(tagName);
  const tagObj = getTagByNameOrAlias(lookup || tagName);
  if (tagObj && getForbiddenCategories(options).includes(tagObj.c)) return true;
  if (options.allowNsfw === true || getNsfwEnabled()) return false;
  return promptTokenIsFilteredNsfw(tagName);
}

const nsfwOmitMemo = new WeakMap();

const tagOrAliasIsNsfw = (tagObj) => {
  if (promptContainsNsfw(tagObj.t)) return true;
  const aliases = Array.isArray(tagObj.a) ? tagObj.a : [];
  return aliases.some((alias) => promptContainsNsfw(alias));
};

const shouldOmitNsfwAutocompleteTag = (tagObj, omitNsfw) => {
  if (!omitNsfw || !tagObj) return false;
  if (tagObj.c === 7) return true;
  const keywords = getNsfwKeywords();
  const hit = nsfwOmitMemo.get(tagObj);
  if (hit && hit.keywords === keywords) return hit.omit;
  const omit = tagOrAliasIsNsfw(tagObj);
  nsfwOmitMemo.set(tagObj, { keywords, omit });
  return omit;
};

// @param {string} tagName - Tag name or alias
// @returns {string} Canonical tag name or original
function resolveAlias(tagName) {
  const tagObj = getTagByNameOrAlias(tagName);
  return tagObj ? tagObj.t : tagName;
}

// Search with autocomplete: exact → prefix → alias → contains
// @param {string} input - Search term
// @param {number} limit - Max results (default: 20)
// @returns {Array} Matching tag objects with matchType/displayText
function searchTags(input, limit = 20, options = {}) {
  if (!tagsData || !input) {
    return [];
  }

  const searchTerm = normalizeTagForLookup(input).toLowerCase();
  if (!searchTerm) {
    return [];
  }
  const forbiddenCategories = getForbiddenCategories(options);
  const forbidAppearanceCountTags = options.forbidAppearanceCountTags === true;
  const suggestionIsForbidden = (tagObj) =>
    forbiddenCategories.includes(tagObj.c) || (forbidAppearanceCountTags && isAppearanceCountTag(tagObj.t));
  const omitNsfw = !getNsfwEnabled();
  const candidates = omitNsfw
    ? tagsData.filter((tagObj) => !shouldOmitNsfwAutocompleteTag(tagObj, true))
    : tagsData;

  const results = [];
  const seen = new Set(); // Prevent duplicates

  // Pass 1: exact matches
  for (const tagObj of candidates) {
    if (results.length >= limit) break;

    if (tagObj.t.toLowerCase() === searchTerm && !seen.has(tagObj.t)) {
      results.push({
        ...tagObj,
        matchType: 'exact',
        displayText: tagObj.t,
        isForbidden: suggestionIsForbidden(tagObj)
      });
      seen.add(tagObj.t);
    }
  }

  // Pass 2: prefix matches
  for (const tagObj of candidates) {
    if (results.length >= limit) break;

    if (tagObj.t.toLowerCase().startsWith(searchTerm) &&
      tagObj.t.toLowerCase() !== searchTerm &&
      !seen.has(tagObj.t)) {
      results.push({
        ...tagObj,
        matchType: 'prefix',
        displayText: tagObj.t,
        isForbidden: suggestionIsForbidden(tagObj)
      });
      seen.add(tagObj.t);
    }
  }

  // Pass 3: alias matches
  for (const tagObj of candidates) {
    if (results.length >= limit) break;

    if (!seen.has(tagObj.t)) {
      for (const alias of tagObj.a) {
        if (alias.toLowerCase().startsWith(searchTerm)) {
          results.push({
            ...tagObj,
            matchType: 'alias',
            displayText: `${alias} → ${tagObj.t}`,
            matchedAlias: alias,
            isForbidden: suggestionIsForbidden(tagObj)
          });
          seen.add(tagObj.t);
          break; // One alias match per tag
        }
      }
    }
  }

  // Pass 4: substring matches
  for (const tagObj of candidates) {
    if (results.length >= limit) break;

    if (!seen.has(tagObj.t) && tagObj.t.toLowerCase().includes(searchTerm)) {
      results.push({
        ...tagObj,
        matchType: 'contains',
        displayText: tagObj.t,
        isForbidden: suggestionIsForbidden(tagObj)
      });
      seen.add(tagObj.t);
    }
  }

  // Sort: non-forbidden first, then match type, then popularity
  return results.sort((a, b) => {
    if (a.isForbidden !== b.isForbidden) {
      return a.isForbidden ? 1 : -1;
    }

    const matchTypePriority = { exact: 0, prefix: 1, alias: 2, contains: 3 };
    const aPriority = matchTypePriority[a.matchType] || 4;
    const bPriority = matchTypePriority[b.matchType] || 4;

    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    return (b.n || 0) - (a.n || 0); // Popularity
  });
}

// @param {string} input - Comma-separated tags
// @param {string} tagToRemove - Tag to remove
// @returns {string} Input with tag removed
function removeTagFromInput(input, tagToRemove) {
  if (!input) {
    return input;
  }

  const tags = input.split(',').map(tag => tag.trim()).filter(tag => tag);
  const filteredTags = tags.filter(tag => {
    const resolvedTag = resolveAlias(tag);
    const resolvedTagToRemove = resolveAlias(tagToRemove);
    return resolvedTag.toLowerCase() !== resolvedTagToRemove.toLowerCase();
  });

  return filteredTags.length > 0 ? filteredTags.join(', ') + ', ' : '';
}

// Peel SD attention wrappers: (tag:0.8), ((tag)), [tag:0.8]. Leaves series tags like name_(series) alone.
function isAttentionInnerBalanced(value) {
  let parenDepth = 0;
  let bracketDepth = 0;

  for (const ch of value) {
    if (ch === '(') {
      parenDepth += 1;
      continue;
    }
    if (ch === ')') {
      parenDepth -= 1;
      if (parenDepth < 0) return false;
      continue;
    }
    if (ch === '[') {
      bracketDepth += 1;
      continue;
    }
    if (ch === ']') {
      bracketDepth -= 1;
      if (bracketDepth < 0) return false;
    }
  }

  return parenDepth === 0 && bracketDepth === 0;
}

function peelAttentionOnce(tagName) {
  const isParenWrapped = tagName.startsWith('(') && tagName.endsWith(')');
  const isBracketWrapped = tagName.startsWith('[') && tagName.endsWith(']');
  if (!isParenWrapped && !isBracketWrapped) return tagName;

  const inner = tagName.slice(1, -1);
  const weightMatch = inner.match(/^(.*):([\d.]+)$/);
  if (weightMatch && isAttentionInnerBalanced(weightMatch[1])) {
    return weightMatch[1].trim();
  }

  return inner.trim();
}

function peelAttentionSyntax(tagName) {
  let current = String(tagName || '').trim();
  if (!current) return '';

  for (;;) {
    const next = peelAttentionOnce(current);
    if (next === current) break;
    current = next;
  }

  return current;
}

function hasAttentionSyntax(tagName) {
  const trimmed = String(tagName || '').trim();
  return Boolean(trimmed) && peelAttentionSyntax(trimmed) !== trimmed;
}

function normalizeTagForLookup(tagName) {
  if (!tagName) {
    return '';
  }

  return peelAttentionSyntax(tagName)
    .replace(/\\\(/g, '(')
    .replace(/\\\)/g, ')')
    .replace(/\s+/g, '_');
}

const pushUniquePromptToken = (found, seen, token) => {
  const text = String(token || '').trim();
  if (!text) return;
  const key = text.toLowerCase();
  if (seen.has(key)) return;
  seen.add(key);
  found.push(text);
};

const isLinkedBraceTokenText = (text, isLinkedBraceToken) => {
  const match = String(text || '').trim().match(/^\{([^{}]+)\}$/);
  if (!match || typeof isLinkedBraceToken !== 'function') return false;
  return isLinkedBraceToken(String(match[1] || '').trim());
};

function collectUnrecognizedPromptTokens(promptText, options = {}) {
  const found = [];
  const seen = new Set();
  tokenizePromptForHighlight(promptText).forEach((part) => {
    if (part.kind === 'plain') return;
    const text = String(part.text || '').trim();
    if (!text) return;
    if (part.kind === 'unrecognized' || /[\[\]]/.test(text)) {
      pushUniquePromptToken(found, seen, text);
      return;
    }
    if (isLinkedBraceTokenText(text, options.isLinkedBraceToken)) return;
    if (/^\{[^{}]+\}$/.test(text)) {
      pushUniquePromptToken(found, seen, text);
      return;
    }
    const lookup = normalizeTagForLookup(text);
    if (!lookup) return;
    if (isRatingPromptTag(lookup)) return;
    if (!getTagByNameOrAlias(lookup)) pushUniquePromptToken(found, seen, text);
  });
  return found;
}

async function getBlockedFilteredImagePromptMessage(promptText) {
  if (getNsfwEnabled()) return null;
  const source = String(promptText || '').trim();
  if (!source) return null;
  await loadNsfwKeywords();
  await loadDanbooruTags();
  const blocked = [];
  const seen = new Set();
  tokenizePromptForHighlight(source).forEach((part) => {
    if (part.kind === 'plain') return;
    const text = String(part.text || '').trim();
    if (!text || !promptTokenIsFilteredNsfw(text)) return;
    const key = (normalizeTagForLookup(text) || text).toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    blocked.push(text);
  });
  if (blocked.length === 0) return null;
  return getFilteredNsfwPromptMessage(blocked);
}

async function getBlockedUnrecognizedPromptMessage(promptText, options = {}) {
  if (getNsfwEnabled()) return null;
  const source = String(promptText || '').trim();
  if (!source) return null;
  await loadDanbooruTags();
  if (!tagIndex) return null;
  const blocked = collectUnrecognizedPromptTokens(source, options);
  if (blocked.length === 0) return null;
  return getFilteredUnrecognizedPromptMessage(blocked);
}

function parsePromptTags(promptText) {
  if (!promptText) {
    return [];
  }

  return promptText.split(',').map((tag) => tag.trim()).filter(Boolean);
}

function getPromptForbiddenCategoryTags(promptText, categories = FAST_PREVIEW_FORBIDDEN_CATEGORIES) {
  if (!promptText || !categories?.length) {
    return [];
  }

  const found = [];
  const seen = new Set();

  parsePromptTags(promptText).forEach((tag) => {
    const lookupTagName = normalizeTagForLookup(tag);
    if (!lookupTagName) {
      return;
    }

    const tagObj = getTagByNameOrAlias(lookupTagName);
    if (!tagObj || !categories.includes(tagObj.c)) {
      return;
    }

    const canonicalTag = tagObj.t;
    if (seen.has(canonicalTag)) {
      return;
    }

    seen.add(canonicalTag);
    found.push(canonicalTag);
  });

  return found;
}

function getFastPreviewBlockingTags(appearanceText, outfitText) {
  const found = [
    ...getPromptForbiddenCategoryTags(appearanceText),
    ...getPromptForbiddenCategoryTags(outfitText),
  ];
  const seen = new Set();

  return found.filter((tag) => {
    if (seen.has(tag)) {
      return false;
    }

    seen.add(tag);
    return true;
  });
}

function promptHasForbiddenCategory(promptText, categories = FAST_PREVIEW_FORBIDDEN_CATEGORIES) {
  return getPromptForbiddenCategoryTags(promptText, categories).length > 0;
}

function hasFastPreviewBlockingTags(appearanceText, outfitText) {
  return getFastPreviewBlockingTags(appearanceText, outfitText).length > 0;
}

const CHARACTER_PROMPT_SANITIZE_OPTIONS = {
  suggestionType: 'characterPrompt',
  forbiddenCategories: [6, 7],
};

const OUTFIT_PROMPT_SANITIZE_OPTIONS = {
  suggestionType: 'outfitPrompt',
  forbiddenCategories: [3, 4, 7],
};

function normalizePromptTagKey(tag) {
  return String(tag || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

function uniquePromptTags(tags) {
  const seen = new Set();
  const unique = [];
  tags.forEach((tag) => {
    const key = normalizePromptTagKey(tag);
    if (!key || seen.has(key)) return;
    seen.add(key);
    unique.push(tag.trim());
  });
  return unique;
}

function splitPromptTagTokens(promptText) {
  return String(promptText || '')
    .split(/[,\n]/)
    .map((token) => token.trim())
    .filter(Boolean);
}

function isTagForbiddenForContext(tag, options = {}) {
  const lookupTag = normalizeTagForLookup(tag);
  if (!lookupTag) return false;
  if (isForbiddenTag(lookupTag, options)) return true;
  if (options.suggestionType === 'characterPrompt') {
    if (isBodyPromptToken(tag)) return true;
    if (isLocationDenylistTag(tag)) return true;
  }
  return false;
}

function sanitizePromptTags(text, options = {}) {
  if (!text) return '';

  const tokens = splitPromptTagTokens(text);
  const kept = [];

  tokens.forEach((token) => {
    if (isTagForbiddenForContext(token, options)) return;
    const trimmed = token.trim();
    // Keep user-written weights/emphasis intact after forbidden filtering
    if (hasAttentionSyntax(trimmed)) {
      kept.push(trimmed);
      return;
    }
    const resolved = resolveAlias(normalizeTagForLookup(trimmed));
    const displayTag = resolved.includes('_') ? resolved.replace(/_/g, ' ') : trimmed;
    kept.push(displayTag);
  });

  return uniquePromptTags(kept).join(', ');
}

function formatPromptForDisplay(tags) {
  if (!Array.isArray(tags) || !tags.length) return '';
  const joined = uniquePromptTags(tags.filter(Boolean)).join(', ');
  if (!joined) return '';
  return joined.endsWith(',') ? joined : `${joined},`;
}

function partitionAppearanceAndOutfit(characterText, outfitText) {
  const appearanceTokens = [];
  const outfitTokens = splitPromptTagTokens(outfitText);
  const outfitSeen = new Set(outfitTokens.map(normalizePromptTagKey));

  splitPromptTagTokens(characterText).forEach((token) => {
    const key = normalizePromptTagKey(token);
    if (!key) return;

    if (isTagForbiddenForContext(token, CHARACTER_PROMPT_SANITIZE_OPTIONS)) {
      if (!outfitSeen.has(key)) {
        outfitTokens.push(token);
        outfitSeen.add(key);
      }
      return;
    }

    appearanceTokens.push(token);
  });

  const appearance = sanitizePromptTags(
    uniquePromptTags(appearanceTokens).join(', '),
    CHARACTER_PROMPT_SANITIZE_OPTIONS,
  );
  const outfit = sanitizePromptTags(
    uniquePromptTags(outfitTokens).join(', '),
    OUTFIT_PROMPT_SANITIZE_OPTIONS,
  );

  return { appearance, outfit };
}

const resolveCharacterTagName = (danbooruCharacterTag) => {
  if (!danbooruCharacterTag || typeof danbooruCharacterTag !== 'string') return null;

  const searchTerm = danbooruCharacterTag.trim().toLowerCase().replace(/-/g, '_');
  if (!searchTerm) return null;

  const directMatch = getTagByNameOrAlias(searchTerm);
  if (directMatch?.t) return directMatch.t;

  const nameParts = searchTerm.split('_').filter((part) => part.length > 0);
  const permutations = nameParts.length >= 2
    ? [nameParts.join('_'), [...nameParts].reverse().join('_')]
    : nameParts;

  for (const perm of permutations) {
    const match = getTagByNameOrAlias(perm);
    if (match?.t) return match.t;
  }

  return null;
};

const prependMatchedCharacterTag = ({ danbooruCharacterTag, characterPrompt = '' }) => {
  const matchedTag = resolveCharacterTagName(danbooruCharacterTag);
  if (!matchedTag) return String(characterPrompt || '').trim();

  const existingPrompt = String(characterPrompt || '').trim();
  const existingTags = existingPrompt
    .split(',')
    .map((tag) => tag.trim().toLowerCase().replace(/\s+/g, '_'));
  const matchedKey = matchedTag.toLowerCase();
  if (existingTags.includes(matchedKey)) return existingPrompt;

  const combined = existingPrompt ? `${matchedTag}, ${existingPrompt}` : matchedTag;
  return combined.replace(/, ,/g, ',').replace(/^,|,$/g, '').trim();
};

export {
  loadDanbooruTags,
  searchTags,
  isForbiddenTag,
  isRecognizedTag,
  resolveAlias,
  removeTagFromInput,
  getTagByNameOrAlias,
  isRatingPromptTag,
  normalizeTagForLookup,
  collectUnrecognizedPromptTokens,
  getBlockedFilteredImagePromptMessage,
  getBlockedUnrecognizedPromptMessage,
  parsePromptTags,
  promptHasForbiddenCategory,
  getPromptForbiddenCategoryTags,
  getFastPreviewBlockingTags,
  hasFastPreviewBlockingTags,
  sanitizePromptTags,
  partitionAppearanceAndOutfit,
  prependMatchedCharacterTag,
  formatPromptForDisplay,
};
